import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { notifyError } from '@/core/notify/notify';
import { encodeUriQuery } from '@/core/routing/matrix';
import { NO_PERIOD_NOTE, periodRangeText } from '@/core/format/periodLabel';
import type { PeriodRow } from '@/core/models/practice.models';
import type { ReportingCodeNode } from '@/core/models/phase1.models';
import { Button } from '@/kit/Button';
import { FormField, InputField } from '@/kit/FormField';
import type { CharModeOptions } from '@/kit/inputCharMode';
import { Icon } from '@/kit/Icon';
import { DialogClose } from '@/kit/dialog';

/* ------------------------------------------------------------------------------------------ formatting */

const twoDecimals = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'negative',
});
const noDecimals = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  signDisplay: 'negative',
});

/** Angular `number:'1.2-2'`: empty for null/undefined/'' (the pipe returns null), otherwise grouped with 2 decimals. */
export function fmt2(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : twoDecimals.format(n);
}

/** Angular `number:'1.0-0'`. */
export function fmt0(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : noDecimals.format(n);
}

/** `<input type="number" [(ngModel)]>`: an empty box is null, anything else its number. */
export function numberFromInput(text: string): number | null {
  return text === '' ? null : Number(text);
}

/** Angular `input value` of a number-or-null model. */
export function numberToInput(value: number | null | undefined): string | number {
  return value === null || value === undefined ? '' : value;
}

/* ------------------------------------------------------------------------------------------ component state */

/**
 * The Angular component's mutable fields: `patch` updates them synchronously (readable at once through `ref`, like
 * `this.x` right after an ngModel change) and re-renders.
 */
export function useLive<T extends object>(initial: T) {
  const [state, setState] = useState<T>(initial);
  const ref = useRef<T>(state);
  const patch = useCallback((changes: Partial<T>) => {
    ref.current = { ...ref.current, ...changes };
    setState(ref.current);
  }, []);
  return [state, patch, ref] as const;
}

/** True while mounted: async results are dropped after the screen has gone (ngOnDestroy). Survives StrictMode. */
export function useAlive() {
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
}

/** `ngOnInit`: runs once even though StrictMode mounts effects twice in development. */
export function useOnInit(init: () => void) {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * The per-client guard of every screen in this slice: no client -> toast, back to Accounts, and `init` never runs.
 * Returns a ref holding the client (Angular's private `client` field).
 */
export function useClientInit(init: (client: SelectedClient) => void) {
  const navigate = useNavigate();
  const clientRef = useRef<SelectedClient | null>(null);
  useOnInit(() => {
    clientRef.current = selectedClient();
    if (!clientRef.current) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    init(clientRef.current);
  });
  return clientRef;
}

/* ------------------------------------------------------------------------------------------ small helpers */

/** `{{ item.fromDate }} - {{ item.toDate }}` (and the `to` variant of the phase 2 screens). */
// APT355: a missing date is never printed as the word undefined or null (no period gives '' and the dropdown's placeholder shows).
export const periodDash = (item: PeriodRow) => periodRangeText(item?.fromDate, item?.toDate, ' - ');
export const periodTo = (item: PeriodRow) => periodRangeText(item?.fromDate, item?.toDate, ' to ');

/** APT355: what a screen that needs an accounting period says under its dropdown while the client has none (shown once the periods have loaded). */
export function NoPeriodNote({ show }: { show: boolean }) {
  return show ? <p className="text-gray font-size-12 no-period-note">{NO_PERIOD_NOTE}</p> : null;
}

/** ImportStatus (Capium.AccountsProduction.Contracts.Common): Staged=0, Committed=1, Superseded=2, Failed=3. */
export function importBatchStatusName(status: number): string {
  return status === 1 ? 'Committed' : status === 2 ? 'Superseded' : status === 3 ? 'Failed' : 'Staged';
}

/** Angular `{{ value | date: 'dd-MMM-yyyy HH:mm' }}`, so two same-shaped batches in a list are still told apart. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD-MMM-YYYY HH:mm') : '';
}

/**
 * A postable reporting code as a pick list offers it. APT335: `code` (to match the codes the API offers for an account) and `group` (the
 * statement section it is on, so the long list is grouped as Profit and loss / Balance sheet / Capital and reserves).
 */
export interface ReportingCodeOption {
  id: string;
  name: string;
  code?: string;
  group?: string;
}

const SECTION_NAMES: Record<number, string> = { 0: 'Profit and loss account', 1: 'Balance sheet', 2: 'Capital and reserves' };

/** The tree is for browsing; the pick lists need the flat postable codes, in tree order. */
export function flattenReportingCodes(nodes: ReportingCodeNode[]): ReportingCodeOption[] {
  const result: ReportingCodeOption[] = [];
  const walk = (list: ReportingCodeNode[]) => {
    (list ?? []).forEach((node) => {
      if (node.isPostable) {
        result.push({
          id: node.reportingCodeId,
          name: `${node.code} - ${node.description}`,
          code: node.code,
          group: SECTION_NAMES[node.statementArea] ?? 'Other',
        });
      }
      walk(node.children);
    });
  };
  walk(nodes);
  return result;
}

/**
 * The statutory database and validation centre Fix handlers: `router.navigate([path], { queryParams })` with the query
 * parsed by hand (values containing `=` are truncated, keys are not decoded), serialised the way Angular does.
 */
export function fixUrl(route: string): string {
  const [path, query] = route.split('?');
  const params: Record<string, string> = {};
  (query ?? '')
    .split('&')
    .filter(Boolean)
    .forEach((pair) => {
      const [key, value] = pair.split('=');
      params[key] = decodeURIComponent(value ?? '');
    });
  const serialised = Object.entries(params).map(([key, value]) => `${encodeUriQuery(key)}=${encodeUriQuery(value)}`);
  return serialised.length > 0 ? `${path}?${serialised.join('&')}` : path;
}

/* ------------------------------------------------------------------------------------------ native `change` */

/**
 * A text input's native `(change)` event: it fires when focus leaves (or Enter is pressed) after the value was edited,
 * not on every keystroke. `onCommit` is that event; it runs after the model has been updated by `onChange`.
 */
export function useNativeChange(value: string, onCommit: () => void) {
  const baseline = useRef(value);
  const fire = () => {
    if (baseline.current !== value) {
      baseline.current = value;
      onCommit();
    }
  };
  return {
    onFocus: () => {
      baseline.current = value;
    },
    onBlur: fire,
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') fire();
    },
  };
}

interface ChangeInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'prefix'>, CharModeOptions {
  value: string;
  onValueChange: (value: string) => void;
  /** Angular `(change)`. */
  onCommit: () => void;
  fieldClassName?: string;
}

/** `<mat-form-field appearance="outline"><input matInput [(ngModel)] (change)>`. */
export function ChangeInput({ value, onValueChange, onCommit, onFocus, onBlur, onKeyDown, ...rest }: ChangeInputProps) {
  const native = useNativeChange(value, onCommit);
  return (
    <InputField
      {...rest}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onValueChange(event.target.value)}
      onFocus={(event: FocusEvent<HTMLInputElement>) => {
        native.onFocus();
        onFocus?.(event);
      }}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        native.onBlur();
        onBlur?.(event);
      }}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        native.onKeyDown(event);
        onKeyDown?.(event);
      }}
    />
  );
}

interface SearchFieldProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Angular `(change)` on the box; omit when the screen listens to `(ngModelChange)` only. */
  onCommit?: () => void;
  placeholder?: string;
}

/**
 * `<mat-form-field appearance="outline" class="search-field"><mat-icon>search</mat-icon><input matInput ...>`: the icon
 * is projected into the infix next to the input, like the Angular markup.
 */
export function SearchField({ value, onValueChange, onCommit, placeholder = 'Search' }: SearchFieldProps) {
  const [focused, setFocused] = useState(false);
  const native = useNativeChange(value, () => onCommit?.());
  return (
    <FormField className="search-field" focused={focused}>
      <Icon name="search" />
      <input
        className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
        placeholder={placeholder}
        autoComplete="off"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        onFocus={() => {
          setFocused(true);
          native.onFocus();
        }}
        onBlur={() => {
          setFocused(false);
          native.onBlur();
        }}
        onKeyDown={native.onKeyDown}
      />
    </FormField>
  );
}

/* ------------------------------------------------------------------------------------------ exports */

/** The three direct export actions (format 2 Excel, 1 CSV, 3 PDF), always visible - no dropdown. */
export function ExportMenu({ onExport }: { onExport: (format: number) => void }) {
  return (
    <>
      <Button title="Export to Excel" onClick={() => onExport(2)}>
        <Icon name="border_all" className="icon-color-xls" />
      </Button>
      <Button title="Export to CSV" onClick={() => onExport(1)}>
        <Icon name="description" outlined className="icon-color-xls" />
      </Button>
      <Button title="Export to PDF" onClick={() => onExport(3)}>
        <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
      </Button>
    </>
  );
}

/** `<button mat-button class="action-button secondary-button" mat-dialog-close>Cancel</button>`. */
export function CancelButton({ children = 'Cancel' }: { children?: ReactNode }) {
  return (
    <DialogClose
      as="button"
      type="button"
      className="mdc-button mat-mdc-button mat-unthemed mat-mdc-button-base action-button secondary-button"
    >
      <span className="mdc-button__label">{children}</span>
    </DialogClose>
  );
}
